import React, { useState } from 'react'
import { useMutation } from 'react-query'

import { useApi } from '../../../hooks'
import { formatToLocalTime } from '../../../utils/dates'
import toast from '../../toast'

// "Who answered" line under an answered question (AB#38328). Answers given
// before attribution existed carry no source and show nothing, as before.
const attributionLabel = (service, vendorName) => {
  if (!service || !service.sensorAnswer) return null
  let who = null
  if (service.sensorAnswerSource === 'client') who = 'you'
  else if (service.sensorAnswerSource === 'staff') who = vendorName || 'your service provider'
  if (!who) return null
  const when = service.sensorAnsweredAt ? ` · ${formatToLocalTime(service.sensorAnsweredAt)}` : ''
  return `Answered by ${who}${when}`
}

// "Requires Attention" — safety feature questions surfaced by adasThink.
// Answering a question cascades service status changes on the report
// (equipped → required, not-equipped → hidden, null → potential).
//
// Read-only unless the shop lets this client answer (canAnswerQuestions, from
// the report's own client — AB#38328). A closed report stays read-only even when
// converted: the API's closed guard refuses answers there (unlike readiness).
const SafetyFeatures = ({ report, canAnswerQuestions, vendorName, refetch }) => {
  const { adasReportService } = useApi()
  const [savingSensor, setSavingSensor] = useState(null)
  const canAnswer = !!canAnswerQuestions && report.status !== 'closed'

  const mutation = useMutation(
    ({ sensorName, answer }) => adasReportService.answerSensorQuestion({ id: report._id, sensorName, answer }),
    {
      retry: false,
      onSuccess: () => {
        toast.success({ title: 'Updated', body: 'Your answer was saved.' })
        // Keep the question in its saving state until the report has reloaded.
        return Promise.resolve(refetch && refetch()).finally(() => setSavingSensor(null))
      },
      onError: (err) => {
        toast.error({ title: 'Error', body: (err && err.message) || 'Failed to save your answer.' })
        setSavingSensor(null)
      },
    }
  )

  const answer = (sensorName, value) => {
    if (!canAnswer || mutation.isLoading) return
    setSavingSensor(sensorName)
    mutation.mutate({ sensorName, answer: value })
  }

  // Match Admin portal behavior: question list is based on services, not the
  // raw safetyFeatures array. This avoids over-rendering unrelated features.
  const services = (report.adas && report.adas.services) || []
  const potentialSensors = []
  const seen = new Set()
  services.forEach(svc => {
    // A service the shop REMOVED is off the report — its sensorAnswer must not
    // resurrect the sensor into this customer-facing card. A not-equipped
    // service stays: that is an answered "No", which the card shows with its
    // answer selected, same as the admin portal (#38204).
    if (svc.recommendedStatus === 'removed') return
    if ((svc.recommendedStatus === 'potential' || svc.sensorAnswer) && !seen.has(svc.sensorName)) {
      seen.add(svc.sensorName)
      potentialSensors.push(svc.sensorName)
    }
  })

  if (!potentialSensors.length) return null

  const cardClass = canAnswer
    ? 'adas-detail__attention-card'
    : 'adas-detail__attention-card adas-detail__attention-card--readonly'

  return (
    <div className={cardClass}>
      <div className='adas-detail__attention-header'>
        <h3 className='adas-detail__attention-title'>Requires Attention</h3>
        <p className='adas-detail__attention-subtitle'>
          Answering these questions will determine if &quot;Potential&quot; services are required.
        </p>
      </div>

      {potentialSensors.map((sensorName, idx) => {
        const currentService = services.find(s => s.sensorName === sensorName)
        const currentAnswer = (currentService && currentService.sensorAnswer) || null
        // AB#38328 ships dark: who answered is shown only to a client the shop lets answer.
        const attribution = canAnswerQuestions ? attributionLabel(currentService, vendorName) : null
        const isSaving = savingSensor === sensorName
        const disabled = !canAnswer || mutation.isLoading
        const canReset = canAnswer && !mutation.isLoading && currentAnswer !== null
        return (
          <div key={sensorName} className='adas-detail__attention-question'>
            <div className='adas-detail__question-body'>
              <span className='adas-detail__question-text'>
                {idx + 1}. Is the vehicle equipped with {sensorName}?
              </span>
              {attribution && (
                <span className='adas-detail__question-attribution'>{attribution}</span>
              )}
            </div>
            <div className='adas-detail__question-actions'>
              <div className='adas-detail__radio-group'>
                <label className='adas-detail__radio'>
                  <input
                    type='radio'
                    name={`question-${sensorName}`}
                    value='yes'
                    checked={currentAnswer === 'equipped'}
                    disabled={disabled}
                    readOnly={!canAnswer}
                    onChange={canAnswer ? () => answer(sensorName, 'equipped') : undefined}
                  />
                  <span>Yes</span>
                </label>
                <label className='adas-detail__radio'>
                  <input
                    type='radio'
                    name={`question-${sensorName}`}
                    value='no'
                    checked={currentAnswer === 'not-equipped'}
                    disabled={disabled}
                    readOnly={!canAnswer}
                    onChange={canAnswer ? () => answer(sensorName, 'not-equipped') : undefined}
                  />
                  <span>No</span>
                </label>
              </div>
              {isSaving ? (
                <span className='adas-detail__reset-link is-disabled' aria-live='polite'>
                  Saving…
                </span>
              ) : canReset ? (
                <span
                  className='adas-detail__reset-link'
                  role='button'
                  tabIndex={0}
                  onClick={() => answer(sensorName, null)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') answer(sensorName, null) }}
                >
                  Reset
                </span>
              ) : (
                <span className='adas-detail__reset-link is-disabled'>
                  Reset
                </span>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default SafetyFeatures
